import React from 'react';
import ReactECharts from 'echarts-for-react';
import { useEmotionData } from './EmotionData';
const EmotionCountBarChart = ({ artworkId, embedded = false }) => {
  const { processedData,loading,error } = useEmotionData(artworkId);
  const shellClass = embedded
    ? 'w-full h-full flex items-center justify-center text-center text-white'
    : 'w-full max-w-3xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800 flex items-center justify-center h-full text-center text-white';

  if (loading) {
    return <div className={shellClass}>Loading emotion data...</div>;
  }

  if (error) {
    return <div className={`${shellClass} text-red-400`}>{error}</div>;
  }

  if (!processedData.barChart || processedData.barChart.length === 0) {
    return <div className={`${shellClass} text-gray-300`}>No emotion data available for this artwork</div>;
  }

  // 3-color gradients for each emotion - Bright, shining colors
  const getEmotionGradient = (emotion) => {
    const gradients = {
      positive: {
        type: 'linear',
        x: 0, y: 1, x2: 0, y2: 0,
        colorStops: [
          { offset: 0, color: '#00ff88' },   // Bright Neon Green
          { offset: 0.5, color: '#00ffaa' },  // Vibrant Cyan-Green
          { offset: 1, color: '#00ffcc' }     // Shining Aqua
        ]
      },
      neutral: {
        type: 'linear',
        x: 0, y: 1, x2: 0, y2: 0,
        colorStops: [
          { offset: 0, color: '#ffb800' },   // Bright Gold
          { offset: 0.5, color: '#ffd700' },  // Shining Yellow-Gold
          { offset: 1, color: '#fff700' }     // Brilliant Yellow
        ]
      },
      negative: {
        type: 'linear',
        x: 0, y: 1, x2: 0, y2: 0,
        colorStops: [
          { offset: 0, color: '#ff3366' },   // Bright Pink-Red
          { offset: 0.5, color: '#ff0066' },  // Vibrant Magenta-Red
          { offset: 1, color: '#ff0088' }     // Shining Hot Pink
        ]
      }
    };
    return gradients[emotion] || '#9E9E9E';
  };

  const emotionGlowColors = {
    positive: "rgba(0, 255, 136, 0.9)",     // Bright Neon Green glow
    neutral: "rgba(255, 184, 0, 0.9)",      // Bright Gold glow
    negative: "rgba(255, 51, 102, 0.9)"     // Bright Pink-Red glow
  };

  const option = {
    title: {
      text: 'Emotion Distribution',
      left: 'center',
      bottom: '3%',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#ffffff'
      }
    },
    tooltip: {
      trigger: 'axis',
      appendToBody: embedded,
      axisPointer: {
        type: 'shadow',
        shadowStyle: {
          color: 'rgba(150, 150, 150, 0.2)'
        }
      },
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'rgba(255, 255, 255, 0.3)',
      borderWidth: 0,
      textStyle: {
        color: '#fff',
        fontSize: 14
      },
      padding: [10, 15]
    },
    grid: {
      left: '0%',
      right: '0%',
      top: '8%',
      bottom: '18%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: processedData.barChart.map(item => item.emotion.charAt(0).toUpperCase() + item.emotion.slice(1)),
      axisLabel: {
        fontSize: 12,
        fontWeight: 'normal',
        color: '#e0e0e0',
        margin: 10
      },
      axisLine: {
        lineStyle: {
          color: '#666666',
          width: 2
        }
      },
      axisTick: {
        show: false
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value}',
        fontSize: 13,
        fontWeight: 'bold',
        color: '#e0e0e0'
      },
      axisLine: {
        lineStyle: {
          color: '#666666',
          width: 2
        }
      },
      splitLine: {
        lineStyle: {
          color: '#444444',
          type: 'dashed',
          width: 1
        }
      }
    },
    series: [
      {
        name: 'Count',
        type: 'bar',
        barWidth: '50%',
        data: processedData.barChart.map((item, index) => ({
          value: item.count,
          itemStyle: {
            color: getEmotionGradient(item.emotion),
            shadowBlur: 25,
            shadowColor: emotionGlowColors[item.emotion] || "rgba(158, 158, 158, 0.9)",
            shadowOffsetX: 0,
            shadowOffsetY: 8,
            borderRadius: [8, 8, 0, 0]
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 40,
              shadowColor: emotionGlowColors[item.emotion] || "rgba(158, 158, 158, 1)",
              shadowOffsetY: 12
            },
            focus: 'self'
          }
        })),
        label: {
          show: true,
          position: 'top',
          formatter: '{c}',
          fontSize: 13,
          fontWeight: 'bold',
          color: '#ffffff'
        },
        emphasis: {
          focus: 'series',
          label: {
            fontSize: 16,
            fontWeight: 'bold'
          },
          itemStyle: {
            shadowBlur: 45
          }
        },
        animationDelay: function (idx) {
          return idx * 150;
        }
      }
    ],
    animationEasing: 'elasticOut',
    animationDelayUpdate: function (idx) {
      return idx * 5;
    }
  };

  return (
    <div className={embedded ? 'w-full h-full' : 'w-full max-w-3xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800'}>
      <div className={embedded ? 'h-full' : 'h-80'}>
        <ReactECharts option={option} style={{ height: '100%', width: '100%' }} />
      </div>
    </div>
  );
};

export default EmotionCountBarChart;